import { memo, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Grid,
    IconButton,
    Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { rectSortingStrategy } from '@dnd-kit/sortable';
import {
    restrictToFirstScrollableAncestor,
    restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import { Sortable } from '@travelity/ui';
import type { Attachment } from '@travelity/api/services';

import DeleteIcon from '@mui/icons-material/Delete';
import { buildItemId, normalizeImage } from './helpers/imagesGrid.helpers';

export type UploadedImagesGridProps = {
    images: Attachment[];
    onChange?: (next: Attachment[]) => void;
    readOnly?: boolean;
};

const UploadedImagesGrid = ({
    images,
    onChange,
    readOnly = false,
}: UploadedImagesGridProps) => {
    const items = useMemo(
        () => images.map(image => normalizeImage(image)),
        [images]
    );
    const keyedItems = useMemo(
        () =>
            images.map((image, index) => ({
                id: buildItemId(image, index),
                image,
                meta: items[index],
            })),
        [images, items]
    );

    const handleRemoveAtIndex = (index: number) => {
        if (!onChange) return;
        onChange(images.filter((_, i) => i !== index));
    };

    const handleReorder = (nextIds: string[]) => {
        if (!onChange) return;
        const byId = new Map(keyedItems.map(item => [item.id, item.image]));
        const next = nextIds
            .map(id => byId.get(id))
            .filter((image): image is Attachment => Boolean(image));
        onChange(next);
    };

    if (!items.length) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No images uploaded yet.
            </Typography>
        );
    }

    const content = (
        index: number,
        dnd?: Record<string, any>,
        key?: string
    ) => {
        const item = keyedItems[index];
        if (!item) return null;
        const { meta } = item;
        const ref = dnd?.ref;
        const style = dnd?.style;
        const listeners = dnd?.listeners;
        const setActivatorNodeRef = dnd?.setActivatorNodeRef;

        return (
            <Grid item xs={12} sm={6} md={4} ref={ref} style={style} key={key}>
                <Card variant="outlined" sx={{ position: 'relative' }}>
                    {!readOnly && (
                        <IconButton
                            size="small"
                            aria-label={`Remove image ${meta.name}`}
                            onClick={() => handleRemoveAtIndex(index)}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                zIndex: 1,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}

                    {!readOnly && (
                        <Box
                            ref={setActivatorNodeRef}
                            {...listeners}
                            aria-label={`Reorder image ${meta.name}`}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                zIndex: 1,
                                bgcolor: 'background.paper',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'grab',
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </Box>
                    )}

                    {meta.src ? (
                        <CardMedia
                            component="img"
                            height="140"
                            image={meta.src}
                            alt={`Preview of ${meta.name}`}
                            sx={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: 140,
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                            >
                                No preview
                            </Typography>
                        </Box>
                    )}
                    <CardContent sx={{ pb: 2 }}>
                        <Typography variant="body2" noWrap>
                            {meta.name}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    return (
        <Grid container spacing={2}>
            {readOnly ? (
                keyedItems.map((item, index) =>
                    content(index, undefined, item.id)
                )
            ) : (
                <Sortable
                    items={keyedItems.map(item => item.id)}
                    setItems={handleReorder}
                    strategy={rectSortingStrategy}
                    modifiers={[
                        restrictToFirstScrollableAncestor,
                        restrictToWindowEdges,
                    ]}
                    useDragOverlay={false}
                    // @ts-ignore
                    renderItem={dnd => content(dnd.index, dnd)}
                />
            )}
        </Grid>
    );
};

export default memo(UploadedImagesGrid);
